import { useEffect, useState, useRef } from 'react';
import { Droplets, Wind, MapPin, Search, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import { wmoLabel } from '../lib/utils';

const DAY_SHORT = { da: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'], en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'], sv: ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'], no: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'] };

export default function WeatherWidget() {
    const { user, refresh } = useAuth();
    const { locale, t } = useI18n();
    const [weather, setWeather] = useState(null);
    const [city, setCity] = useState(user?.location?.city || '');
    const [editing, setEditing] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const debounceRef = useRef(null);

    // Default to Copenhagen ONLY as final fallback; auto-detect geolocation if user hasn't set one.
    const lat = user?.location?.lat ?? 55.6761;
    const lon = user?.location?.lon ?? 12.5683;

    useEffect(() => {
        api.get('/weather', { params: { lat, lon } })
            .then((r) => setWeather(r.data))
            .catch(() => setWeather(null));
        if (user?.location?.city) setCity(user.location.city);
    }, [lat, lon, user?.location?.city]);

    // Geolocation auto-prompt is owned by <LocationOnboardModal/> on Dashboard.
    // Widget exposes a manual "Use my location" button inside the edit panel below.

    const requestGeo = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(async (pos) => {
            const la = pos.coords.latitude, lo = pos.coords.longitude;
            try {
                const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${la}&lon=${lo}&format=json&accept-language=${locale}`);
                const j = await r.json();
                const c = j?.address?.city || j?.address?.town || j?.address?.village || j?.address?.county || 'My location';
                await api.patch('/auth/me', { location: { lat: la, lon: lo, city: c, postcode: j?.address?.postcode } });
                refresh();
                setEditing(false);
            } catch {}
        }, () => {}, { timeout: 8000 });
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query || query.length < 2) { setResults([]); return; }
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const r = await api.get('/geocode', { params: { q: query } });
                setResults(r.data.results || []);
            } catch { setResults([]); }
            setSearching(false);
        }, 250);
    }, [query]);

    const pickCity = async (item) => {
        try {
            await api.patch('/auth/me', { location: { lat: item.lat, lon: item.lon, city: item.name, postcode: item.postcode } });
            refresh();
            setEditing(false);
            setQuery('');
            setResults([]);
        } catch {}
    };

    const cur = weather?.current;
    const daily = weather?.daily;
    const w = cur ? wmoLabel(cur.weather_code, locale) : { icon: '🌤️', label: '...' };
    const days = daily?.time?.slice(0, 5) || [];
    const labels = DAY_SHORT[locale] || DAY_SHORT.en || DAY_SHORT.da;

    // Frost detection — return next day with min < 2°C
    const frostDayIdx = daily ? daily.temperature_2m_min?.findIndex((v, i) => i <= 2 && v < 2) : -1;

    return (
        <div data-testid="weather-widget" className="bg-brand-card border border-brand-border rounded-3xl p-6 relative">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-xl font-serif text-brand-ink">{t('home.weather')}</h3>
                    <button
                        data-testid="btn-set-location"
                        onClick={() => setEditing(true)}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-brand-ink3 hover:text-brand-green transition-colors"
                    >
                        <MapPin size={12} strokeWidth={1.6} />
                        <span>{city || t('weather.detect_location') || 'Set location'}</span>
                    </button>
                </div>
                <div className="text-5xl" aria-hidden>{w.icon}</div>
            </div>

            <div className="flex items-end gap-3">
                <div className="text-5xl font-serif text-brand-ink leading-none">
                    {cur ? Math.round(cur.temperature_2m) : '—'}°
                </div>
                <div className="pb-2 text-sm text-brand-ink2">{w.label}</div>
            </div>

            <div className="mt-4 flex gap-5 text-xs text-brand-ink3">
                <span className="inline-flex items-center gap-1.5"><Droplets size={14} strokeWidth={1.6} /> {cur?.relative_humidity_2m ?? '—'}%</span>
                <span className="inline-flex items-center gap-1.5"><Wind size={14} strokeWidth={1.6} /> {cur ? Math.round(cur.wind_speed_10m) : '—'} m/s</span>
            </div>

            {daily && (
                <div className="mt-5 grid grid-cols-5 gap-2 pt-4 border-t border-brand-border">
                    {days.map((d, i) => {
                        const date = new Date(d);
                        const wm = wmoLabel(daily.weather_code[i], locale);
                        const isFrost = daily.temperature_2m_min[i] < 2;
                        return (
                            <div key={d} className={`flex flex-col items-center gap-1 ${isFrost ? 'text-brand-terracotta' : ''}`}>
                                <span className={`text-[10px] uppercase tracking-widest font-semibold ${isFrost ? 'text-brand-terracotta' : 'text-brand-ink3'}`}>
                                    {i === 0 ? t('weather.today') : labels[date.getDay()]}
                                </span>
                                <span className="text-xl">{wm.icon}</span>
                                <span className={`text-xs ${isFrost ? 'text-brand-terracotta' : 'text-brand-ink'}`}>{Math.round(daily.temperature_2m_max[i])}°</span>
                                <span className={`text-[10px] ${isFrost ? 'text-brand-terracotta font-bold' : 'text-brand-ink3'}`}>{Math.round(daily.temperature_2m_min[i])}°</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {frostDayIdx >= 0 && (
                <div data-testid="frost-warning-mini" className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-brand-terracotta">
                    ❄️ {t('weather.frost_risk')}
                </div>
            )}

            {editing && (
                <div data-testid="city-search" className="absolute inset-0 bg-brand-card border border-brand-border rounded-3xl p-6 z-10">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-serif text-lg">{t('weather.choose_location')}</h3>
                        <button onClick={() => { setEditing(false); setQuery(''); setResults([]); }} className="p-2 hover:bg-brand-bg2 rounded-full"><X size={16} /></button>
                    </div>
                    <button data-testid="btn-geo-locate" onClick={requestGeo} className="w-full inline-flex items-center justify-center gap-2 bg-brand-green text-white py-3 rounded-full text-sm mb-3 hover:bg-brand-greenDark">
                        <MapPin size={15} /> {t('weather.use_my_loc')}
                    </button>
                    <div className="relative">
                        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-ink3" />
                        <input
                            data-testid="input-city-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('weather.search_city')}
                            className="w-full bg-brand-bg border border-brand-border rounded-full pl-11 pr-4 py-3 text-sm"
                        />
                    </div>
                    <div className="mt-3 space-y-1 max-h-60 overflow-y-auto">
                        {searching && <p className="text-xs text-brand-ink3 text-center py-3">{t('weather.searching')}</p>}
                        {results.map((r, i) => (
                            <button
                                key={`${r.name}-${i}`}
                                data-testid={`city-result-${i}`}
                                onClick={() => pickCity(r)}
                                className="block w-full text-left px-4 py-2.5 rounded-2xl hover:bg-brand-bg2 transition-colors"
                            >
                                <div className="text-sm text-brand-ink">{r.name}</div>
                                <div className="text-xs text-brand-ink3">{[r.admin1, r.country].filter(Boolean).join(', ')}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
